import { DocumentType } from '../../../../enums/document-type.enum';

/**
 * Patient document entity
 */
export interface PatientDocument {
  id: string;
  patientId?: string;
  woundId?: string;
  originalFileName: string;
  storedFileName?: string;
  fileType: DocumentType;
  filePath?: string;
  fileSize: number;
  mimeType: string;
  createdDate: Date;
  uploadedById?: string;
  uploadedByName: string;
}

/**
 * Document group for timeline display
 */
export interface DocumentGroup {
  date: Date;
  dateKey: string;
  documents: PatientDocument[];
}

/**
 * Wound document group for wound-based display
 */
export interface WoundDocumentGroup {
  woundId: string;
  woundCreatedDate?: string | Date;
  woundStatus?: number;
  documents: PatientDocument[];
}

/**
 * Request model for uploading a document
 */
export interface UploadDocumentRequest {
  file: File;
  patientId: string;
  woundId?: string;
}

/**
 * Response model for document upload
 */
export interface UploadDocumentResponse {
  data: PatientDocument;
  isSuccess: boolean;
  message?: string;
}

/**
 * Response model for document list
 */
export interface GetDocumentsResponse {
  data: PatientDocument[];
  isSuccess: boolean;
  message?: string;
}

export interface GetListDocumentByPatientModel {
  id: string;
  originalFileName: string;
  fileType: string;
  fileSize: number;
  mimeType: string;
  createdDate: Date;
  uploadedByName?: string;
  woundId?: string;
}

export interface GetListDocumentByWoundModel {
  id: string;
  originalFileName: string;
  fileType: string;
  fileSize: number;
  mimeType: string;
  createdDate: Date;
  uploadedByName?: string;
  woundId?: string;
}

export interface UploadDocumentModel {
  id: string;
  originalFileName: string;
  createdDate: Date;
}

/**
 * Format document filename with upload date
 * @param originalName Original filename
 * @param uploadDate Upload date
 * @returns Formatted filename with date suffix
 */
export function formatDocumentFileName(
  originalName: string,
  uploadDate: Date
): string {
  const extension = originalName.split('.').pop() || '';
  const baseName = originalName.replace(/\.[^/.]+$/, '');
  const year = uploadDate.getFullYear();
  const month = String(uploadDate.getMonth() + 1).padStart(2, '0');
  const day = String(uploadDate.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;
  return `${baseName}_${dateStr}.${extension}`;
}

/**
 * Group documents by date for timeline display
 * @param documents Array of documents
 * @returns Array of document groups sorted by date (newest first)
 */
export function groupDocumentsByDate(
  documents: PatientDocument[]
): DocumentGroup[] {
  const groups: Map<string, DocumentGroup> = new Map();

  for (const doc of documents) {
    const date = new Date(doc.createdDate);
    const dateKey = date.toISOString().split('T')[0];

    if (groups.has(dateKey)) {
      groups.get(dateKey)!.documents.push(doc);
    } else {
      groups.set(dateKey, {
        date: date,
        dateKey: dateKey,
        documents: [doc],
      });
    }
  }

  return Array.from(groups.values()).sort(
    (a, b) => new Date(b.dateKey).getTime() - new Date(a.dateKey).getTime()
  );
}

/**
 * Group documents by wound for wound-based display
 * @param documents Array of documents
 * @param wounds Array of wounds to get creation dates
 * @returns Array of wound document groups
 */
export function groupDocumentsByWound(
  documents: PatientDocument[],
  wounds: any[] = []
): WoundDocumentGroup[] {
  const groups: Map<string, WoundDocumentGroup> = new Map();

  for (const wound of wounds) {
    if (!groups.has(wound.id)) {
      groups.set(wound.id, {
        woundId: wound.id,
        woundCreatedDate: wound.createdDateTime,
        woundStatus: wound.woundStatus,
        documents: [],
      });
    }
  }

  for (const doc of documents) {
    const rawDoc = doc as any;
    let foundWoundId: string | null = null;

    const extractGuids = (input: any): string[] => {
      if (!input || typeof input !== 'string') return [];
      const guidRegex =
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
      return input.match(guidRegex) || [];
    };

    const findParentWoundId = (targetGuid: string): string | null => {
      const tg = targetGuid.toLowerCase();
      for (const w of wounds) {
        if (String(w.id).toLowerCase() === tg) return w.id;
        if (w.recordId && String(w.recordId).toLowerCase() === tg) return w.id;

        const checkRecursive = (list: any[]): boolean => {
          for (const f of list) {
            if (String(f.id).toLowerCase() === tg) return true;
            if (f.recordId && String(f.recordId).toLowerCase() === tg)
              return true;
            if (f.followUps && checkRecursive(f.followUps)) return true;
          }
          return false;
        };
        if (w.followUps && checkRecursive(w.followUps)) return w.id;
      }
      return null;
    };

    const keys = Object.keys(rawDoc).sort((a, b) => {
      const aL = a.toLowerCase();
      const bL = b.toLowerCase();
      const aScore =
        (aL.includes('wound') ? 10 : 0) +
        (aL.includes('id') || aL.includes('record') ? 5 : 0);
      const bScore =
        (bL.includes('wound') ? 10 : 0) +
        (bL.includes('id') || bL.includes('record') ? 5 : 0);
      return bScore - aScore;
    });

    for (const key of keys) {
      const guids = extractGuids(rawDoc[key]);
      for (const guid of guids) {
        const parentId = findParentWoundId(guid);
        if (parentId) {
          foundWoundId = parentId;
          break;
        }
      }
      if (foundWoundId) break;
    }

    if (foundWoundId && groups.has(foundWoundId)) {
      groups.get(foundWoundId)!.documents.push(doc);
    } else {
      if (groups.has('unassigned')) {
        groups.get('unassigned')!.documents.push(doc);
      } else {
        groups.set('unassigned', {
          woundId: 'unassigned',
          documents: [doc],
        });
      }
    }
  }

  return Array.from(groups.values())
    .filter((g) => g.documents.length > 0 || g.woundId !== 'unassigned')
    .sort((a, b) => {
      if (a.woundId === 'unassigned') return 1;
      if (b.woundId === 'unassigned') return -1;

      const aDate = a.woundCreatedDate
        ? new Date(a.woundCreatedDate).getTime()
        : 0;
      const bDate = b.woundCreatedDate
        ? new Date(b.woundCreatedDate).getTime()
        : 0;

      return bDate - aDate;
    });
}
